<template>
    <div class="relative">
        <CircleIconWrapper
            v-if="selected && markSelected"
            color="none"
            class="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-interaction-gradient rounded-full flex items-center justify-center"
        >
            <CheckIcon class="text-white w-4 h-4 mt-0.5"
        /></CircleIconWrapper>

        <div
            :class="[
                wrapperClasses,
                textClasses,
                'flex items-center justify-center px-3 py-1.5',
            ]"
            @click="handleClick"
        >
            <slot name="prepend" />
            <span>
                <slot />
            </span>

            <XIcon
                v-if="removable"
                class="ml-2 w-4 h-4 cursor-pointer transform hover:scale-105"
                appearence="thick"
                @click.stop="handleRemove"
            />
        </div>
    </div>
</template>

<script setup>
import CheckIcon from "@/Components/Icons/CheckIcon.vue";
import CircleIconWrapper from "@/Components/Icons/CircleIconWrapper.vue";
import XIcon from "@/Components/Icons/XIcon.vue";
import { computed } from "vue";

const props = defineProps({
    color: {
        type: String,
        validator(value) {
            return ["green", "white", "gray", "red"].includes(value);
        },
        default: "white",
    },
    wrapperClass: {
        type: [String, Array, Object],
        default: null,
    },
    textClass: {
        type: [String, Array, Object],
        default: "",
    },
    removable: {
        type: Boolean,
        default: false,
    },
    interactive: {
        type: Boolean,
        default: false,
    },
    selected: {
        type: Boolean,
        default: false,
    },
    markSelected: {
        type: Boolean,
        default: true,
    },
    common: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["click", "remove"]);

const wrapperClasses = computed(() => {
    return [
        {
            "bg-green-interaction-gradient": props.color === "green",
            "bg-white": props.color === "white",
            "bg-gray-blueish": props.color === "gray",
            "border border-gray-500 border-dashed": props.color === "red",
            "border border-gray-700":
                props.color === "white" && !props.selected,
            "hover:border-lemon-green hover:border-2 hover:-mx-[1px] hover:-my-[1px]":
                props.color === "white" && !props.selected && props.interactive,
            "cursor-pointer": props.interactive,
            "border-lemon-green": props.selected || props.common,
            "border-2 -mx-[1px] -my-[1px]": props.selected,
            border: props.common,
        },
        props.wrapperClass,
        props.interactive ? "rounded-3xl" : "rounded-2xl",
    ];
});

const textClasses = computed(() => {
    return [
        {
            "text-white": props.color === "green",
            "text-gray-700":
                (props.color === "white" || props.color === "gray") &&
                !props.selected,
            "hover:text-gray-900":
                (props.color === "white" || props.color === "gray") &&
                !props.selected &&
                props.interactive,
            "text-gray-900":
                (props.color === "white" || props.color === "gray") &&
                props.selected,
            "text-red-light": props.color === "red",
            "font-semibold": props.common,
        },
        props.textClass,
    ];
});

const handleClick = () => {
    if (!props.interactive) return;

    emit("click");
};

const handleRemove = () => {
    emit("remove");
};
</script>
